import React, { useState, useEffect, useCallback } from 'react';
import { flagsAPI } from '../../api';
import { formatRelativeTime } from '../../utils';
import { Card, Button, Select, Badge, Spinner, EmptyState } from '../../components/ui';
import { Shield, AlertTriangle } from 'lucide-react';

export const FlagsPage = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');

  const loadFlags = useCallback(async () => {
    try {
      const response = await flagsAPI.list(filter || undefined);
      setFlags(response.data);
    } catch (err) {
      console.error('Failed to load flags:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const handleReview = async (flagId, status) => {
    try {
      await flagsAPI.review(flagId, status, '');
      loadFlags();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to review flag');
    }
  };

  return (
    <div className="space-y-6" data-testid="flags-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Suspicious Flags</h1>
          <p className="text-gray-500">Review flagged activities</p>
        </div>
        <Select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'reviewed', label: 'Reviewed' },
            { value: 'dismissed', label: 'Dismissed' },
            { value: '', label: 'All' },
          ]}
          className="w-32"
        />
      </div>

      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : flags.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No flags"
            description="Suspicious activities will appear here for review"
          />
        ) : (
          <div className="divide-y">
            {flags.map(flag => (
              <div key={flag.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      <p className="font-medium">{flag.flag_type?.replace('_', ' ')}</p>
                      <Badge variant={flag.status === 'open' ? 'warning' : 'info'}>
                        {flag.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{flag.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      User: {flag.user_name} • {formatRelativeTime(flag.created_at)}
                    </p>
                  </div>
                  {flag.status === 'open' && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handleReview(flag.id, 'dismissed')}>
                        Dismiss
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => handleReview(flag.id, 'confirmed')}>
                        Confirm
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default FlagsPage;
